import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LogOut, Warehouse } from 'lucide-react';
import { OperatorSheet } from './OperatorSheet';
import { Button } from '../../../components/ui/Button';
import { Switch } from '../../../components/ui/Switch';
import { logoutUser } from '../../../features/auth/authThunks';
import { operatorPrefChanged, operatorWarehouseSelected } from '../../../features/operator/operatorSlice';
import { operatorDisplayName, selectOperatorPrefs } from '../../../features/operator/operatorUtils';
import { canVibrate } from '../../../services/operatorSignals';

/**
 * Who is signed in, this device's choices, changing warehouse, and signing
 * out. Changing warehouse goes back to Inicio, where it is chosen: never in
 * the middle of a task.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 */
export const OperatorMenu = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { profile, warehouses } = useSelector((state) => state.operator);
  const prefs = useSelector(selectOperatorPrefs);

  const setPref = (pref) => (value) => dispatch(operatorPrefChanged({ pref, value }));

  const changeWarehouse = () => {
    dispatch(operatorWarehouseSelected(null));
    onClose();
    navigate('/dashboard');
  };

  return (
    <OperatorSheet isOpen={isOpen} onClose={onClose} title="Menú" testId="operator-menu">
      <div className="space-y-5">
        <div className="text-center">
          <p className="text-lg font-bold text-ninja-dark dark:text-white">{operatorDisplayName(profile)}</p>
          {profile?.email && <p className="text-sm text-text-secondary">{profile.email}</p>}
          {profile?.employer?.name && <p className="text-sm text-text-secondary">{profile.employer.name}</p>}
        </div>

        {/* A column: `Switch` is inline, and side by side two of them share a line. */}
        <div className="flex flex-col items-start gap-4 rounded-2xl border border-border p-4">
          <Switch checked={prefs.sound} onChange={setPref('sound')} label="Sonido" testId="operator-menu-sound" />
          {canVibrate() && (
            <Switch
              checked={prefs.vibration}
              onChange={setPref('vibration')}
              label="Vibración"
              testId="operator-menu-vibration"
            />
          )}
          <Switch checked={prefs.camera} onChange={setPref('camera')} label="Cámara" testId="operator-menu-camera" />
          {prefs.camera && (
            <Switch
              checked={prefs.cameraDiagnostics}
              onChange={setPref('cameraDiagnostics')}
              label="Diagnóstico de la cámara"
              testId="operator-menu-camera-diagnostics"
            />
          )}
        </div>

        {warehouses.length > 1 && (
          <Button
            variant="outline"
            size="lg"
            fullWidth
            icon={Warehouse}
            onClick={changeWarehouse}
            data-testid="operator-menu-change-warehouse"
          >
            Cambiar bodega
          </Button>
        )}

        <Button
          variant="danger"
          size="lg"
          fullWidth
          icon={LogOut}
          onClick={() => dispatch(logoutUser(navigate))}
          data-testid="operator-logout"
        >
          Cerrar sesión
        </Button>
      </div>
    </OperatorSheet>
  );
};
